import {useCallback, useEffect, useState} from 'react';

/** breathing room under the panel; larger than this is visibly wasted on a short screen */
const BOTTOM_GAP = 24;
const MIN_HEIGHT = 260;

/**
 * Gives a block whatever vertical room is left below it, so it scrolls on its
 * own instead of stretching the page.
 *
 * A `100vh - constant` value cannot know how much the dashboard header and the
 * tab rows above already took, so the position is measured. The bottom edge
 * comes from the nearest scrolling ancestor - the dashboard scrolls its own
 * container, not the window - minus that container's padding.
 */
export default function useAvailableHeight(ref, deps = []) {
    const [height, setHeight] = useState(null);

    const measure = useCallback(() => {
        const node = ref.current;
        if (!node) return;

        const scrollParent = findScrollParent(node);
        let available;

        if (scrollParent) {
            // Measured in the container's own coordinates, not the viewport's. A
            // viewport-relative top shrinks as the page is scrolled down, which
            // made the result grow, which let the page scroll further still.
            const offsetTop = node.getBoundingClientRect().top
                - scrollParent.getBoundingClientRect().top
                + scrollParent.scrollTop;

            // only the bottom padding: clientHeight already includes both, and
            // offsetTop is measured from the container's border edge, so the top
            // padding is inside offsetTop already. Subtracting it twice left that
            // much space unused at the bottom of the panel.
            const paddingBottom = parseFloat(window.getComputedStyle(scrollParent).paddingBottom) || 0;

            available = scrollParent.clientHeight - offsetTop - paddingBottom - BOTTOM_GAP;
        } else {
            // no scrolling ancestor: the window scrolls, and the document offset is
            // what stays put while it does
            const offsetTop = node.getBoundingClientRect().top + window.pageYOffset;
            available = window.innerHeight + window.pageYOffset - offsetTop - BOTTOM_GAP;
        }

        setHeight(Math.max(MIN_HEIGHT, available));
    }, [ref]);

    useEffect(() => {
        measure();
        window.addEventListener('resize', measure);

        return () => window.removeEventListener('resize', measure);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [measure, ...deps]);

    return height;
}

/**
 * The nearest ancestor that scrolls, judged by its style rather than by whether
 * it happens to overflow at this moment: while the panel's content is still
 * loading nothing overflows yet, and the window bottom is the wrong edge to
 * measure against.
 */
function findScrollParent(node) {
    let el = node.parentElement;

    while (el && el !== document.body) {
        const {overflowY} = window.getComputedStyle(el);
        if (overflowY === 'auto' || overflowY === 'scroll') return el;
        el = el.parentElement;
    }

    return null;
}



// WEBPACK FOOTER //
// ./src/utils/hooks/useAvailableHeight.js